import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../../../ui-kitchen/src/utils/sanitizeString';
import { handleMktAnalytics } from '../../../AmexUtils';
import { CTA_EVENT_NAME } from '../../../constants';
import './LayoutCarouselStaticSlide.scss';

const LayoutCarouselStaticSlide = (
  {
    awsFolderName,
    baseRequestUrl,
    slideConfig: {
      image,
      logo,
      logo_alt: logoAlt,
      copy = '',
      media_outlet: mediaOutlet = '',
      title = '',
      cta: {
        label = '',
        url = '',
      } = {},
      shop_name: shopName = '',
    },
    subLayout,
    Analytics,
    tabHeading: heading,
  }
) => {
  const sanitizedMediaOutlet = sanitizeString(mediaOutlet);
  const sanitizedTitle = sanitizeString(title);
  const sanitizedCopy = sanitizeString(copy);
  const urlPrefix = `${baseRequestUrl}${awsFolderName}/carouselImages/`;
  const imageSrc = `${urlPrefix}tabContentImages/${image}`;
  const logoSrc = `${urlPrefix}logos/${logo}`;

  const handleClickWithTagging = () => {
    const specificParamsAmplitude = {
      item_name: shopName || (mediaOutlet ? title : ''),
      action_name: `${label} Click`,
      tab_item: heading,
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, specificParamsAmplitude);
  };

  const renderLogo = () => logo && logoAlt && (
    <h3 className="LayoutCarouselStaticSlide__logo">
      <img src={logoSrc} alt={logoAlt} />
    </h3>
  );

  const renderMediaOutlet = () => mediaOutlet && (
    <p
      className="LayoutCarouselStaticSlide__media"
      dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
        __html: sanitizedMediaOutlet,
      }}
    />
  );

  const renderTitle = () => title && (
    <h3
      className="LayoutCarouselStaticSlide__title"
      dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
        __html: sanitizedTitle,
      }}
    />
  );

  const renderCta = () => {
    if (!label || !url) return null;
    return (
      <div className="LayoutCarouselStaticSlide__footer">
        <Link
          className="LayoutCarouselStaticSlide__cta"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={copy}
          href={url}
          {...(shopName || mediaOutlet) && { onClick: () => handleClickWithTagging() }}
        >
          {label}
        </Link>
      </div>
    );
  };

  const renderCopy = () => copy && (
    <div
      className="LayoutCarouselStaticSlide__text"
      dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
        __html: sanitizedCopy,
      }}
    />
  );

  const renderCardContent = () => {
    switch (subLayout) {
      case 'shop':
        return (
          <>
            {renderLogo()}
            {renderCopy()}
            {renderCta()}
          </>
        );
      case 'diningOptions':
        return (
          <>
            {renderTitle()}
            {renderCopy()}
          </>
        );
      case 'media':
      default:
        return (
          <>
            {renderMediaOutlet()}
            {renderTitle()}
            {renderCta()}
          </>
        );
    }
  };

  return (
    <div className={`LayoutCarouselStaticSlide ${subLayout}`}>
      <div className="LayoutCarouselStaticSlide__card">
        <div
          className="LayoutCarouselStaticSlide__image"
          style={{
            backgroundImage: `url(${imageSrc})`,
          }}
        />
        {renderCardContent()}
      </div>
    </div>
  );
};

LayoutCarouselStaticSlide.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  slideConfig: PropTypes.shape({
    cta: PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      action_name: PropTypes.string.isRequired,
    }).isRequired,
    copy: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    logo: PropTypes.string,
    logo_alt: PropTypes.string,
    media_outlet: PropTypes.string,
    shop_name: PropTypes.string,
    title: PropTypes.string,
  }),
  subLayout: PropTypes.string.isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  tabHeading: PropTypes.string.isRequired,
};

LayoutCarouselStaticSlide.defaultProps = {
  slideConfig: {
    logo: '',
    logo_alt: '',
    shop_name: null,
  },
};

export default LayoutCarouselStaticSlide;
